import { useState, useEffect, useMemo } from "react";
import { COI_ELIGIBLE_STATUSES, US_STATES } from "@shared/schema";
import type { Policy, PolicyVehicle } from "@shared/schema";
import { formatVehicleCoiLine } from "@shared/coi-vehicles";
import { keyForVehicle } from "@shared/asset-identity";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import {
  useCreateLossPayee,
  useUpdateLossPayee,
  useDeleteLossPayee,
  type LossPayeeLink,
} from "@/lib/api";
import {
  buildPolicyCoverageGroups,
  countOfferedSelections,
  toggleOfferedSelection,
} from "@/lib/policy-coverages";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { Loader2, Trash2 } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  insuredId: string;
  policies?: Policy[];
  editData?: LossPayeeLink | null;
}

interface FormErrors {
  name?: string;
  email?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  coverages?: string;
}

interface VehicleOption {
  policyId: string;
  // CSR-1855 — stable identity (keyForVehicle: databaseId -> VIN -> content) so
  // VIN-less assets are selectable. `vin` is display/payload metadata only.
  assetKey: string;
  vin: string;
  label: string;
  secondary: string;
  raw: PolicyVehicle;
}

interface VehicleGroup {
  policyId: string;
  policyLabel: string;
  vehicles: VehicleOption[];
}

// CSR-792 / CSR-1855 — composite key for vehicle multi-select. The same asset
// identity can theoretically appear on multiple policies for the same insured;
// the dedicated key prevents collapsing two policy variants of the same asset.
// Keyed on the stable `assetKey` (keyForVehicle) rather than VIN so VIN-less
// assets get a distinct, durable key.
function vehicleKey(policyId: string, assetKey: string): string {
  return `${policyId}::${assetKey}`;
}

function buildVehicleGroups(policies: Policy[]): VehicleGroup[] {
  return policies
    .filter((p) => COI_ELIGIBLE_STATUSES.has((p.status || "").toLowerCase()))
    .map((p) => {
      // CSR-1848 — trailers were split into `policy.trailers` (CSR-665); include
      // them alongside power units so a Loss Payee can be linked to a trailer.
      const raw = [
        ...((p.vehicles as PolicyVehicle[] | null) ?? []),
        ...((p.trailers as PolicyVehicle[] | null) ?? []),
      ];
      const active = raw.filter((v) => {
        if (v.removedAt) return false;
        // CSR-792 spec calls out status==="Inactive" as a defensive filter.
        // PolicyVehicle does not surface `status` in the typed interface, so
        // we cast through Record<string, unknown> to read it without
        // tripping the type checker.
        const status = (v as unknown as Record<string, unknown>).status;
        if (typeof status === "string" && status.toLowerCase() === "inactive") {
          return false;
        }
        return true;
      });
      return {
        policyId: p.id,
        policyLabel: `${p.policyNumber} · ${(p.type || "Policy").toUpperCase()}`,
        vehicles: active.map((v) => {
          const yearMakeModel = [v.year, v.make, v.model]
            .filter((x) => x !== null && x !== undefined && String(x).trim() !== "")
            .join(" ")
            .trim();
          // CSR-1855 — VIN-less assets are now included (no VIN filter). They key
          // on their `databaseId`/content identity instead.
          return {
            policyId: p.id,
            assetKey: keyForVehicle(v),
            vin: v.vin || "",
            label: v.vin || "(no VIN)",
            secondary: yearMakeModel || formatVehicleCoiLine(v) || "",
            raw: v,
          };
        }),
      };
    })
    .filter((g) => g.vehicles.length > 0);
}

export function LossPayeeDialog({
  open,
  onOpenChange,
  insuredId,
  policies = [],
  editData,
}: Props) {
  const { toast } = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const createMutation = useCreateLossPayee(insuredId);
  const updateMutation = useUpdateLossPayee(insuredId);
  const deleteMutation = useDeleteLossPayee(insuredId);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [specialVerbiage, setSpecialVerbiage] = useState("");
  const [selectedCoverages, setSelectedCoverages] = useState<string[]>([]);
  // Vehicle selection uses the composite (policyId::assetKey) key to keep the
  // backing model accurate when the same asset appears on two policies.
  const [selectedVehicleKeys, setSelectedVehicleKeys] = useState<string[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});

  const isEdit = !!editData;

  // Filter policies to COI_ELIGIBLE_STATUSES per spec → "Frontend reads
  // policies from props". The AI dialog uses the looser default; the LP
  // dialog must restrict to active/COI-eligible only.
  const coverageGroups = useMemo(
    () => buildPolicyCoverageGroups(policies, { eligibleStatuses: COI_ELIGIBLE_STATUSES }),
    [policies],
  );
  const allCoverageIds = useMemo(
    () => coverageGroups.flatMap((g) => g.coverages.map((c) => c.id)),
    [coverageGroups],
  );
  // CSR-2148 — a saved link may name a coverage the carrier has since removed;
  // it is kept in the payload but no longer offered, so count what is on offer.
  const selectedOfferedCount = useMemo(
    () => countOfferedSelections(selectedCoverages, allCoverageIds),
    [selectedCoverages, allCoverageIds],
  );

  const vehicleGroups = useMemo(() => buildVehicleGroups(policies), [policies]);
  const allVehicleKeys = useMemo(
    () =>
      vehicleGroups.flatMap((g) =>
        g.vehicles.map((v) => vehicleKey(v.policyId, v.assetKey)),
      ),
    [vehicleGroups],
  );
  const vehicleByKey = useMemo(() => {
    const map = new Map<string, VehicleOption>();
    for (const g of vehicleGroups) {
      for (const v of g.vehicles) {
        map.set(vehicleKey(v.policyId, v.assetKey), v);
      }
    }
    return map;
  }, [vehicleGroups]);

  useEffect(() => {
    if (open) {
      if (editData) {
        setName(editData.name || "");
        setEmail(editData.email || "");
        setStreet(editData.street || "");
        setCity(editData.city || "");
        setState(editData.state || "");
        setZip(editData.zip || "");
        setSpecialVerbiage(editData.specialVerbiage || "");
        setSelectedCoverages(
          Array.isArray(editData.coverageIds) ? editData.coverageIds.map(String) : [],
        );
        // CSR-1855 — recompute the key from the resolved asset (`v.raw`) so it
        // matches the picker's `keyForVehicle(rawAsset)` key exactly, including
        // for legacy links whose stored key was a `vin:` form but whose asset now
        // has a `databaseId`. This is why the response contract needs no
        // `assetKey` field.
        setSelectedVehicleKeys(
          editData.vehicles.map((v) => vehicleKey(v.policyId, keyForVehicle(v.raw))),
        );
      } else {
        setName("");
        setEmail("");
        setStreet("");
        setCity("");
        setState("");
        setZip("");
        setSpecialVerbiage("");
        setSelectedCoverages([]);
        setSelectedVehicleKeys([]);
      }
      setErrors({});
    }
  }, [open, editData]);

  const toggleCoverage = (id: string) => {
    setSelectedCoverages((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  };

  const selectAllCoverages = () => {
    setSelectedCoverages(toggleOfferedSelection(selectedCoverages, allCoverageIds));
  };

  const toggleVehicle = (key: string) => {
    setSelectedVehicleKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const selectAllVehicles = () => {
    setSelectedVehicleKeys(
      selectedVehicleKeys.length === allVehicleKeys.length ? [] : [...allVehicleKeys],
    );
  };

  // CSR-792 — required-field validation runs at SAVE time. The Save/Add
  // button is `disabled` while invalid; the inline errors only render
  // after the user attempts a submit.
  // CSR-792 S-003: ZIP must be 5 digits or ZIP+4 (mirrors server-side regex).
  const ZIP_REGEX = /^\d{5}(-\d{4})?$/;
  const isFormValid =
    name.trim().length > 0 &&
    email.trim().length > 0 &&
    /\S+@\S+\.\S+/.test(email.trim()) &&
    street.trim().length > 0 &&
    city.trim().length > 0 &&
    state.length > 0 &&
    zip.trim().length > 0 &&
    ZIP_REGEX.test(zip.trim()) &&
    selectedOfferedCount >= 1;

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!name.trim()) newErrors.name = "Name is required";
    if (!email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(email.trim()))
      newErrors.email = "Email must be a valid email address";
    if (!street.trim()) newErrors.street = "Street is required";
    if (!city.trim()) newErrors.city = "City is required";
    if (!state) newErrors.state = "State is required";
    if (!zip.trim()) newErrors.zip = "ZIP is required";
    else if (!ZIP_REGEX.test(zip.trim()))
      newErrors.zip = "ZIP must be 5 digits or 5+4 (e.g. 12345 or 12345-6789)";
    // CSR-2148 — count what the picker offers: a link retained against a
    // removed coverage must not satisfy a rule about what the user can see.
    if (selectedOfferedCount === 0)
      newErrors.coverages = "At least one coverage is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildVehiclePayload = (): Array<{
    policyId: string;
    assetKey: string;
    vin: string;
  }> =>
    selectedVehicleKeys
      .map((key) => vehicleByKey.get(key))
      .filter((v): v is VehicleOption => !!v)
      .map((v) => ({ policyId: v.policyId, assetKey: v.assetKey, vin: v.vin }));

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      name: name.trim(),
      email: email.trim(),
      street: street.trim(),
      city: city.trim(),
      state,
      zip: zip.trim(),
      specialVerbiage: specialVerbiage.trim() || null,
      coverageIds: selectedCoverages,
      vehicles: buildVehiclePayload(),
    };

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ lpId: editData!.id, data: payload });
        toast({ title: "Updated", description: "Loss Payee updated successfully." });
      } else {
        await createMutation.mutateAsync(payload);
        toast({ title: "Added", description: "Loss Payee added successfully." });
      }
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Error",
        description: (err as Error).message,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!editData) return;
    const ok = await confirm({
      title: "Remove Loss Payee",
      description: `Remove "${editData.name}" as a Loss Payee?`,
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;

    try {
      await deleteMutation.mutateAsync(editData.id);
      toast({ title: "Deleted", description: "Loss Payee removed." });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Error",
        description: (err as Error).message,
        variant: "destructive",
      });
    }
  };

  const isBusy =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="loss-payee-dialog"
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Loss Payee" : "Add Loss Payee"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the loss payee details and the coverages this Loss Payee applies to."
              : "Add a loss payee to this insured."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="text-sm font-medium">
              Name <span className="text-destructive">*</span>
            </label>
            <Input
              className="mt-1"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Wells Fargo Equipment Finance"
              data-testid="lp-dialog-name"
            />
            {errors.name && (
              <p className="text-xs text-destructive mt-1">{errors.name}</p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium">
              Email <span className="text-destructive">*</span>
            </label>
            <Input
              className="mt-1"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contact@example.com"
              data-testid="lp-dialog-email"
            />
            {errors.email && (
              <p className="text-xs text-destructive mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium">
              Street <span className="text-destructive">*</span>
            </label>
            <Input
              className="mt-1"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              placeholder="123 Main St"
              data-testid="lp-dialog-street"
            />
            {errors.street && (
              <p className="text-xs text-destructive mt-1">{errors.street}</p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium">
                City <span className="text-destructive">*</span>
              </label>
              <Input
                className="mt-1"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City"
                data-testid="lp-dialog-city"
              />
              {errors.city && (
                <p className="text-xs text-destructive mt-1">{errors.city}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">
                State <span className="text-destructive">*</span>
              </label>
              <select
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                value={state}
                onChange={(e) => setState(e.target.value)}
                data-testid="lp-dialog-state"
              >
                <option value="">Select...</option>
                {US_STATES.map((st) => (
                  <option key={st.value} value={st.value}>
                    {st.value} — {st.label}
                  </option>
                ))}
              </select>
              {errors.state && (
                <p className="text-xs text-destructive mt-1">{errors.state}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">
                ZIP <span className="text-destructive">*</span>
              </label>
              <Input
                className="mt-1"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder="12345"
                inputMode="numeric"
                pattern="\d{5}(-\d{4})?"
                maxLength={10}
                data-testid="lp-dialog-zip"
              />
              {errors.zip && (
                <p className="text-xs text-destructive mt-1">{errors.zip}</p>
              )}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Special Verbiage</label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Optional free-form text to include on certificates issued to this Loss Payee.
            </p>
            <textarea
              className="mt-1 w-full min-h-[80px] px-3 py-2 rounded-md border border-input bg-background text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              value={specialVerbiage}
              onChange={(e) => setSpecialVerbiage(e.target.value)}
              placeholder="e.g. Loss Payee per loan agreement..."
              data-testid="lp-dialog-verbiage"
            />
          </div>

          <div>
            <label className="text-sm font-medium">
              Coverages <span className="text-destructive">*</span>
            </label>
            <p className="text-xs text-muted-foreground mt-0.5">
              At least one coverage is required.
            </p>
            {coverageGroups.length === 0 ? (
              <p className="text-xs italic text-muted-foreground mt-2">
                No active policies with coverages on this insured.
              </p>
            ) : (
              <div className="mt-2 rounded-md border border-input overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-input">
                  <span className="text-xs text-muted-foreground">
                    {selectedOfferedCount} of {allCoverageIds.length} selected
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs px-2"
                    onClick={selectAllCoverages}
                  >
                    {selectedOfferedCount === allCoverageIds.length
                      ? "Deselect all"
                      : "Select all"}
                  </Button>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {coverageGroups.map((group) => (
                    <div key={group.policyId}>
                      <div className="px-3 py-1.5 bg-muted/20 border-b border-input">
                        <span className="text-xs font-semibold text-muted-foreground uppercase">
                          {group.policyLabel}
                        </span>
                      </div>
                      {group.coverages.map((cov) => (
                        <label
                          key={cov.id}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-muted/30 cursor-pointer text-sm"
                        >
                          <Checkbox
                            checked={selectedCoverages.includes(cov.id)}
                            onCheckedChange={() => toggleCoverage(cov.id)}
                          />
                          {cov.name}
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {errors.coverages && (
              <p className="text-xs text-destructive mt-1">{errors.coverages}</p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium">Vehicles</label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Optional — link this Loss Payee to specific vehicles for the canonical "is listed
              as Loss Payee for ..." statement.
            </p>
            {vehicleGroups.length === 0 ? (
              <p className="text-xs italic text-muted-foreground mt-2">
                No active vehicles on this insured's policies.
              </p>
            ) : (
              <div className="mt-2 rounded-md border border-input overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-input">
                  <span className="text-xs text-muted-foreground">
                    {selectedVehicleKeys.length} of {allVehicleKeys.length} selected
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs px-2"
                    onClick={selectAllVehicles}
                  >
                    {selectedVehicleKeys.length === allVehicleKeys.length
                      ? "Deselect all"
                      : "Select all"}
                  </Button>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {vehicleGroups.map((group) => (
                    <div key={group.policyId}>
                      <div className="px-3 py-1.5 bg-muted/20 border-b border-input">
                        <span className="text-xs font-semibold text-muted-foreground uppercase">
                          {group.policyLabel}
                        </span>
                      </div>
                      {group.vehicles.map((v) => {
                        const key = vehicleKey(v.policyId, v.assetKey);
                        return (
                          <label
                            key={key}
                            className="flex items-start gap-2 px-3 py-2 hover:bg-muted/30 cursor-pointer text-sm"
                          >
                            <Checkbox
                              className="mt-0.5"
                              checked={selectedVehicleKeys.includes(key)}
                              onCheckedChange={() => toggleVehicle(key)}
                            />
                            <div className="flex flex-col">
                              <span className="font-mono text-xs">{v.label}</span>
                              {v.secondary && (
                                <span className="text-xs text-muted-foreground">
                                  {v.secondary}
                                </span>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          {isEdit ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-1.5"
              onClick={handleDelete}
              disabled={isBusy}
              data-testid="lp-dialog-delete"
            >
              <Trash2 className="w-4 h-4" /> Delete
            </Button>
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isBusy}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isBusy || !isFormValid}
              data-testid="lp-dialog-submit"
            >
              {isBusy ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  {isEdit ? "Saving..." : "Adding..."}
                </>
              ) : isEdit ? (
                "Save"
              ) : (
                "Add"
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {confirmDialog}
    </>
  );
}
